import { useState, useEffect } from 'react';
import { analyticsAPI } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  BarChart3, Users, FileText, TrendingUp, DollarSign, Trophy,
  RefreshCw, Download, Calendar, Target, ShoppingCart, CalendarCheck
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Chart colors
const COLORS = ['#4472C4', '#44B4C4', '#C44472', '#C4A244', '#72C444', '#8B44C4'];
const SCORE_COLORS = {
  '0-40': '#EF4444',
  '41-60': '#F59E0B', 
  '61-80': '#84CC16',
  '81-100': '#22C55E'
};

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodDays, setPeriodDays] = useState(30);

  useEffect(() => {
    loadAnalytics();
  }, [periodDays]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const res = await analyticsAPI.getAdminAnalytics(periodDays);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Gagal memuat analytics');
    } finally {
      setLoading(false);
    }
  };

  const exportResults = async () => {
    try {
      const res = await analyticsAPI.exportResults({});
      const data = res.data.data;
      if (data.length === 0) {
        toast.info('Tidak ada data untuk diekspor');
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hasil-ujian-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('Export berhasil');
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Gagal export data');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', { 
      style: 'currency', 
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatShortCurrency = (amount) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}jt`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}rb`;
    return amount.toString();
  };

  // Prepare chart data from analytics
  const prepareTimeSeriesData = () => {
    if (!analytics?.date_labels) return [];
    
    return analytics.date_labels.map(date => {
      const shortDate = date.slice(5); // MM-DD format
      return {
        date: shortDate,
        fullDate: date,
        users: analytics.user_registrations?.by_day?.[date] || 0,
        submissions: analytics.submissions?.by_day?.[date] || 0,
        revenue: analytics.revenue?.by_day?.[date] || 0
      };
    });
  };

  const prepareScoreDistributionData = () => {
    if (!analytics?.score_distribution) return [];
    return [
      { name: '0-40%', value: analytics.score_distribution['0-40'] || 0, fill: SCORE_COLORS['0-40'] },
      { name: '41-60%', value: analytics.score_distribution['41-60'] || 0, fill: SCORE_COLORS['41-60'] },
      { name: '61-80%', value: analytics.score_distribution['61-80'] || 0, fill: SCORE_COLORS['61-80'] },
      { name: '81-100%', value: analytics.score_distribution['81-100'] || 0, fill: SCORE_COLORS['81-100'] }
    ];
  };

  const prepareProgramDistributionData = () => {
    if (!analytics?.program_distribution) return [];
    return Object.entries(analytics.program_distribution).map(([name, value], index) => ({
      name: name.length > 12 ? name.slice(0, 12) + '...' : name,
      fullName: name,
      value,
      fill: COLORS[index % COLORS.length]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#4472C4]" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Gagal memuat analytics</p>
        <Button onClick={loadAnalytics} className="mt-4">Coba Lagi</Button>
      </div>
    );
  }

  const timeSeriesData = prepareTimeSeriesData();
  const scoreDistributionData = prepareScoreDistributionData();
  const programDistributionData = prepareProgramDistributionData();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h1>
          <p className="text-slate-500">Statistik {analytics.period_days} hari terakhir</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={periodDays.toString()} onValueChange={(v) => setPeriodDays(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Hari</SelectItem>
              <SelectItem value="14">14 Hari</SelectItem>
              <SelectItem value="30">30 Hari</SelectItem>
              <SelectItem value="60">60 Hari</SelectItem>
              <SelectItem value="90">90 Hari</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadAnalytics} data-testid="refresh-analytics">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={exportResults} className="bg-[#4472C4] hover:bg-[#3861a8]" data-testid="export-results">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="card-new-users">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{analytics.user_registrations?.total_new || 0}</p>
              <p className="text-sm text-slate-500">Pendaftar Baru</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4" data-testid="card-total-exams">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{analytics.submissions?.total || 0}</p>
              <p className="text-sm text-slate-500">Total Ujian</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4" data-testid="card-avg-score">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Trophy className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{analytics.submissions?.avg_score || 0}%</p>
              <p className="text-sm text-slate-500">Rata-rata Skor</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4" data-testid="card-revenue">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatCurrency(analytics.revenue?.total || 0)}</p>
              <p className="text-sm text-slate-500">Pendapatan</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Second Row - Events & Purchases */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CalendarCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{analytics.events?.active || 0}</p>
              <p className="text-sm text-slate-500">Event Aktif</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{analytics.events?.total_participants || 0}</p>
              <p className="text-sm text-slate-500">Peserta Event</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{analytics.pending_purchases || 0}</p>
              <p className="text-sm text-slate-500">Pembelian Pending</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{analytics.events?.total || 0}</p>
              <p className="text-sm text-slate-500">Total Event</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Trend Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* User & Submission Trend */}
        <Card className="p-6" data-testid="chart-user-trend">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#4472C4]" />
            Tren Pendaftaran & Ujian
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeSeriesData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4472C4" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#4472C4" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSubmissions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10, fill: '#64748B' }} 
                  interval="preserveStartEnd"
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelFormatter={(value) => `Tanggal: ${value}`}
                />
                <Legend 
                  wrapperStyle={{ fontSize: '12px' }}
                  iconType="circle"
                />
                <Area 
                  type="monotone" 
                  dataKey="users" 
                  stroke="#4472C4" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorUsers)" 
                  name="Pendaftar"
                />
                <Area 
                  type="monotone" 
                  dataKey="submissions" 
                  stroke="#22C55E" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorSubmissions)" 
                  name="Ujian"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Revenue Trend */}
        <Card className="p-6" data-testid="chart-revenue-trend">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#4472C4]" />
            Tren Pendapatan
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeSeriesData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10, fill: '#64748B' }} 
                  interval="preserveStartEnd"
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: '#64748B' }} 
                  tickLine={false} 
                  axisLine={false}
                  tickFormatter={formatShortCurrency}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(value) => [formatCurrency(value), 'Pendapatan']}
                  labelFormatter={(value) => `Tanggal: ${value}`}
                />
                <Bar 
                  dataKey="revenue" 
                  fill="#F59E0B" 
                  radius={[4, 4, 0, 0]}
                  name="Pendapatan"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Distribution Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Score Distribution - Pie Chart */}
        <Card className="p-6" data-testid="chart-score-distribution">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#4472C4]" />
            Distribusi Skor
          </h3>
          <div className="h-64 flex items-center">
            {scoreDistributionData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={scoreDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#64748B', strokeWidth: 1 }}
                  >
                    {scoreDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value} siswa`, name]}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px' }}
                    iconType="circle"
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-slate-500 w-full">Belum ada data skor</p>
            )}
          </div>
        </Card>

        {/* Program Distribution - Horizontal Bar */}
        <Card className="p-6" data-testid="chart-program-distribution">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#4472C4]" />
            Pengerjaan per Program
          </h3>
          <div className="h-64">
            {programDistributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={programDistributionData} 
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#64748B' }} tickLine={false} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 10, fill: '#64748B' }} 
                    width={80}
                    tickLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value, name, props) => [`${value} pengerjaan`, props.payload.fullName]}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {programDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-slate-500 py-8">Belum ada data pengerjaan</p>
            )}
          </div>
        </Card>
      </div>

      {/* Top Tryouts */}
      <Card className="p-6" data-testid="chart-top-tryouts">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#4472C4]" />
          Top 5 Try Out Terpopuler
        </h3>
        {analytics.popular_tryouts?.length > 0 ? (
          <div className="space-y-3">
            {analytics.popular_tryouts.map((tryout, index) => {
              const maxCount = Math.max(...analytics.popular_tryouts.map(t => t.count));
              const percentage = maxCount > 0 ? (tryout.count / maxCount) * 100 : 0;
              return (
                <div key={tryout.id || index}>
                  <div className="flex justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={index < 3 ? 'default' : 'secondary'} 
                        className={index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : ''}
                      >
                        #{index + 1}
                      </Badge>
                      <span className="text-slate-700 truncate max-w-[300px]">{tryout.title}</span>
                    </div>
                    <span className="font-medium text-[#4472C4]">{tryout.count} peserta</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${percentage}%`,
                        backgroundColor: index === 0 ? '#EAB308' : index === 1 ? '#94A3B8' : index === 2 ? '#B45309' : '#4472C4'
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-slate-500 py-8">Belum ada data try out</p>
        )}
      </Card>
    </div>
  );
}
